// Owned by mju-ai-workbench/shared/apps/customViewBridge.ts — the custom view's bridge contract (D354). The AIOE session mirrors it for the phone's react-native-webview; the workbench never mirrors it back. Keep the two identical.
/**
 * The one door between a host and a custom view's sandboxed frame, as data: what the frame may say (`frameMessageSchema`) and what
 * the host says back (`hostMessageSchema`). The desktop frame (webview-ui/src/components/apps/customViewModel.ts, which parses
 * with a hand-written twin because the renderer carries no zod; `uiControlsBridge.test.ts` holds the two to the same corpus) and
 * the phone's react-native-webview speak exactly this, under the same content policy ({@link CUSTOM_VIEW_CONTRACT}).
 *
 * Every message from the frame carries `ch`, the channel key the host put in the page; a message without the right one is dropped.
 * A view cannot make the host do anything these shapes do not name. Pure: zod and constants only.
 */
import { z } from "zod";
const id = z.number().int();
const name = z.string().min(1).max(60);
const record = z.record(z.string(), z.unknown());
/** What the frame may say to the host. */
export const frameMessageSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("ready") }).strict(),
    z.object({ type: z.literal("refresh") }).strict(),
    z.object({ type: z.literal("height"), px: z.number().finite() }).strict(),
    z.object({ type: z.literal("read"), id, name }).strict(),
    z.object({ type: z.literal("run"), id, name, values: record.optional(), row: record.optional() }).strict(),
    z.object({ type: z.literal("violation"), what: z.string().min(1).max(80) }).strict(),
]);
/** What the host says to the frame. */
export const hostMessageSchema = z.discriminatedUnion("type", [
    z.object({ ch: z.string(), type: z.literal("data"), data: record, errors: z.record(z.string(), z.string()), at: z.number() }).strict(),
    z.object({ ch: z.string(), type: z.literal("theme"), vars: z.record(z.string(), z.string()), mode: z.enum(["light", "dark"]) }).strict(),
    z.object({ ch: z.string(), type: z.literal("result"), id, ok: z.boolean(), value: z.unknown().optional(), message: z.string().optional() }).strict(),
]);
/** A frame message as the host reads it: the channel key must be the one it put in the page. Undefined: drop it. */
export function parseFrame(raw, channel) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return undefined;
    const { ch, ...rest } = raw;
    if (ch !== channel)
        return undefined;
    const parsed = frameMessageSchema.safeParse(rest);
    return parsed.success ? parsed.data : undefined;
}
/** What both hosts promise a view, so the phone's WebView and the desktop's frame behave the same. */
export const CUSTOM_VIEW_CONTRACT = {
    /** The frame's content policy: no network, no frames, no forms, inline styles and scripts only. */
    csp: ["default-src 'none'", "script-src 'unsafe-inline'", "style-src 'unsafe-inline'", "img-src data:", "font-src data:", "connect-src 'none'", "frame-src 'none'", "child-src 'none'", "worker-src 'none'", "form-action 'none'", "base-uri 'none'"],
    /** The iframe `sandbox` (desktop) and the equivalent WebView settings (phone: JavaScript on, nothing else: no file access, no cookies, no popups, navigation blocked, `originWhitelist` empty). */
    sandbox: "allow-scripts",
    /** The object the page gets: app.data, app.onData(fn), app.refresh(), app.run(name, { values?, row? }), app.errors. */
    api: ["data", "onData", "refresh", "run", "errors"],
    /** The tallest the view may grow in each place; it reports its content height and the host caps it. */
    maxHeight: { feed: 210, board: 200, conversation: 360, screen: 1600, phone: 1600 },
};
//# sourceMappingURL=customViewBridge.js.map